import { collection, query, where, getDocs, Timestamp } from "../config/dataClient";
import { db } from "../config/firebase";
import { APP_ID } from "../config/constants";
import { buildAttendanceReport, buildPayrollCsv, getReportMonths, requireReportAdmin } from "./reportExports";

export async function loadAttendancePeople(role) {
  requireReportAdmin(role);
  const snapshot = await getDocs(collection(db, "artifacts", APP_ID, "public", "data", "daily_status"));
  return snapshot.docs.map(d => ({ ...d.data(), uid: d.id, name: d.data().fullName || d.data().name || d.id }))
    .sort((a, b) => a.name.localeCompare(b.name, "zh-TW"));
}

export async function loadPayrollPeople(role) {
  requireReportAdmin(role);
  const people = new Map((await loadAttendancePeople(role)).map(person => [person.uid, person]));
  const snapshot = await getDocs(collection(db, "artifacts", APP_ID, "public", "data", "payroll_slips"));
  // Keep former employees selectable even when their personnel record no longer exists.
  const slips = snapshot.docs.map(d => d.data()).sort((a, b) => b.year - a.year || b.month - a.month);
  for (const slip of slips) {
    if (slip.uid && !people.has(slip.uid)) people.set(slip.uid, {
      uid: slip.uid, name: slip.name || slip.uid, accountStatus: "archived",
    });
  }
  return [...people.values()].sort((a, b) => a.name.localeCompare(b.name, "zh-TW"));
}

export async function exportAttendanceRange(role, { start, end, selectedUsers }) {
  requireReportAdmin(role);
  const months = getReportMonths(start, end);
  if (!selectedUsers?.length) throw new Error("請選擇至少一位人員");
  const first = months[0], last = months[months.length - 1];
  const from = Timestamp.fromDate(new Date(first.year, first.month, 1));
  const until = Timestamp.fromDate(new Date(last.year, last.month + 1, 1));
  const datasets = [];
  for (const person of selectedUsers) {
    const logs = await getDocs(query(collection(db, "artifacts", APP_ID, "users", person.uid, "logs"), where("timestamp", ">=", from), where("timestamp", "<", until)));
    const leaves = await getDocs(query(collection(db, "artifacts", APP_ID, "public", "data", "leaves"), where("uid", "==", person.uid)));
    datasets.push({ ...person,
      logs: logs.docs.map(d => ({ ...d.data(), id: d.id, dateObj: d.data().timestamp?.toDate() })).filter(l => l.dateObj),
      leaves: leaves.docs.map(d => d.data()),
    });
  }
  return buildAttendanceReport(datasets, months);
}

export async function exportPayrollRange(role, { start, end, selectedUsers }) {
  requireReportAdmin(role);
  const months = getReportMonths(start, end);
  const selectedPeople = new Map((selectedUsers || []).filter(person => person.uid).map(person => [person.uid, person]));
  const selectedUids = new Set(selectedPeople.keys());
  if (!selectedUids.size) throw new Error("請選擇至少一位人員");
  const keys = new Set(months.map(m => m.key));
  const slips = [];
  // Read persisted historical slips, including departed staff; never generate or recalculate them.
  for (const year of new Set(months.map(m => m.year))) {
    const snapshot = await getDocs(query(collection(db, "artifacts", APP_ID, "public", "data", "payroll_slips"), where("year", "==", year)));
    for (const d of snapshot.docs) {
      const slip = { ...d.data(), id: d.id };
      if (selectedUids.has(slip.uid) && keys.has(`${slip.year}-${String(slip.month).padStart(2, "0")}`)) slips.push(slip);
    }
  }
  if (!slips.length) throw new Error("所選人員與月份區間沒有已建立的薪資單");
  const content = buildPayrollCsv(slips, true);
  const populated = new Map([...selectedUids].map(uid => [uid, new Set()]));
  for (const slip of slips) {
    populated.get(slip.uid).add(`${slip.year}-${String(slip.month).padStart(2, "0")}`);
  }
  // A slip for one employee must not hide another employee's missing month.
  const missing = [...selectedPeople.values()].flatMap(person => {
    const label = person.name ? `${person.name}（${person.uid}）` : person.uid;
    return months.filter(m => !populated.get(person.uid).has(m.key)).map(m => `${label} ${m.key}`);
  });
  return { content, mime: "text/csv;charset=utf-8", filename: `Payroll_${start}_${end}.csv`,
    message: `已匯出 ${slips.length} 份薪資明細`, missing };
}
